import { ErrorBoundary } from '@/components/Rollbar';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import { useGetInvoiceReportPaymentInfo } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import { SmartListViewDetails } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/ApplicationContent/components/SmartListViewDetails';
import { useDataChangeReset } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/hooks/useDataChangeReset';
import { useSetBusinessDocumentsData } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/hooks/useSetBusinessDocumentsData';
import { useApplicationMismatchViewData } from '@/hooks/report_inferences/useApplicationMismatchViewData';
import type { ReportInferencesMismatchViewData } from '@/hooks/report_inferences/useReportInferencesMismatchCaptions';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { FC, memo, Suspense, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

type ApplicationDetailProps = {
  reportId: string;
  paymentTotalAmount?: string | null;
  onMismatchViewDataChange?: Dispatch<
    SetStateAction<ReportInferencesMismatchViewData | undefined>
  >;
};

type InnerListProps = {
  reportId: string;
  paymentTotalAmount?: string | null;
  onMismatchViewDataChange?: Dispatch<
    SetStateAction<ReportInferencesMismatchViewData | undefined>
  >;
};

type ErrorFallbackProps = {
  error: Error | null;
};

const InnerList: FC<InnerListProps> = memo(
  ({ reportId, paymentTotalAmount, onMismatchViewDataChange }) => {
    const { data } = useGetInvoiceReportPaymentInfo(reportId);
    const mismatchViewData = useApplicationMismatchViewData({
      reportId,
      reportFormInputs: data?.report_form_inputs,
      paymentTotalAmount,
      onMismatchViewDataChange,
    });
    useDataChangeReset(data);
    useSetBusinessDocumentsData(data);

    return (
      <SmartListViewDetails
        data={data ?? { report_form_inputs: [] }}
        mismatchViewData={mismatchViewData}
      />
    );
  }
);
InnerList.displayName = 'InnerList';

const ErrorFallback: FC<ErrorFallbackProps> = ({ error }) => {
  const { t } = useTranslation();
  const notify = useToastAPI();
  useEffect(() => {
    notify.error({
      title: error?.message ?? t('system_error'),
      duration: 3,
    });
  }, [error, notify, t]);
  return <></>;
};
ErrorFallback.displayName = 'ErrorFallback';

export const ApplicationDetail: FC<ApplicationDetailProps> = memo(
  ({ reportId, paymentTotalAmount, onMismatchViewDataChange }) => {
    return (
      <Suspense fallback={<SuspenseLoading />}>
        <ErrorBoundary
          fallback={({ error }) => <ErrorFallback error={error} />}
        >
          <InnerList
            reportId={reportId}
            paymentTotalAmount={paymentTotalAmount}
            onMismatchViewDataChange={onMismatchViewDataChange}
          />
        </ErrorBoundary>
      </Suspense>
    );
  }
);

ApplicationDetail.displayName = 'ApplicationDetail';
